/* ===========================================================
   shared/merge3.js — three-way merge of documents

   Used for "Merge changes back" (edit copy → original), for edits
   made offline that met someone else's edits, and for files changed
   on disk while open. Inputs are ProseMirror JSON docs:

     base   = how it looked when the two sides split
     ours   = one side now   (the original, or the online version)
     theirs = other side now (the copy, or this device's offline edits)

   Blocks changed on one side only are taken from that side. Blocks
   changed on both sides are merged deeper where that is safe: inside
   lists, quotes and tables item by item, inside a paragraph word by
   word, inside code line by line. What still clashes becomes a
   conflict the person chooses on, and until they do, the default is
   to KEEP BOTH (text is never dropped silently).

   merge3() returns { doc, conflicts, build, stats }:
     doc        merged doc with every conflict set to "both"
     conflicts  [{ id, base, ours, theirs }]  (arrays of block nodes)
     build(choices)  doc for { [id]: 'ours' | 'theirs' | 'both' | 'base' }
   =========================================================== */

import { canon, marksKey, tidy } from './blocks.js';
import { matchPairs, hunks } from './seqdiff.js';

const CONTAINERS = new Set(['doc', 'bulletList', 'orderedList', 'taskList', 'listItem', 'taskItem',
  'blockquote', 'table', 'sandbox']);
const PROSE = new Set(['paragraph', 'heading']);
const CODE = new Set(['codeBlock', 'sandboxCode']);

class Clash extends Error {}

export function merge3(base, ours, theirs) {
  const conflicts = [];
  const stats = { ours: 0, theirs: 0, both: 0 };
  const ctx = { conflicts, stats };
  const content = mergeSeq(base?.content || [], ours?.content || [], theirs?.content || [], ctx);
  const template = { ...(ours || base || { type: 'doc' }), type: 'doc', content };

  const build = (choices = {}) => tidy(expand(template, conflicts, choices));
  return { doc: build(), conflicts, build, stats, template };
}

/* ---------- the core: diff3 over a list ---------- */

/**
 * Walk base/ours/theirs together. `onBoth(bs, os, ts)` decides a stretch
 * both sides changed and returns the items to use.
 */
function diff3(b, o, t, key, onBoth, stats) {
  const kb = b.map(key), ko = o.map(key), kt = t.map(key);
  const hO = hunks(b.length, o.length, matchPairs(kb, ko));
  const hT = hunks(b.length, t.length, matchPairs(kb, kt));
  const out = [];
  let pos = 0, offO = 0, offT = 0;

  for (const g of group(hO, hT)) {
    for (let i = pos; i < g.bStart; i++) out.push(b[i]);
    const oS = g.bStart + offO;
    for (const h of g.o) offO += (h.sEnd - h.sStart) - (h.bEnd - h.bStart);
    const oE = g.bEnd + offO;
    const tS = g.bStart + offT;
    for (const h of g.t) offT += (h.sEnd - h.sStart) - (h.bEnd - h.bStart);
    const tE = g.bEnd + offT;

    const os = o.slice(oS, oE), ts = t.slice(tS, tE);
    if (!g.t.length) { out.push(...os); if (stats) stats.ours++; }
    else if (!g.o.length) { out.push(...ts); if (stats) stats.theirs++; }
    else {
      const ko2 = os.map(key), kt2 = ts.map(key);
      if (ko2.length === kt2.length && ko2.every((k, i) => k === kt2[i])) out.push(...os);
      else { out.push(...onBoth(b.slice(g.bStart, g.bEnd), os, ts)); if (stats) stats.both++; }
    }
    pos = g.bEnd;
  }
  for (let i = pos; i < b.length; i++) out.push(b[i]);
  return out;
}

/** Merge hunks from both sides into groups whose base ranges overlap. */
function group(hO, hT) {
  const all = [
    ...hO.map(h => ({ ...h, side: 'o' })),
    ...hT.map(h => ({ ...h, side: 't' })),
  ].sort((x, y) => x.bStart - y.bStart || (x.bEnd - x.bStart) - (y.bEnd - y.bStart));
  const groups = [];
  let g = null;
  for (const h of all) {
    if (g && overlaps(g, h)) {
      g.bEnd = Math.max(g.bEnd, h.bEnd);
      g[h.side].push(h);
    } else {
      g = { bStart: h.bStart, bEnd: h.bEnd, o: [], t: [] };
      g[h.side].push(h);
      groups.push(g);
    }
  }
  return groups;
}

function overlaps(g, h) {
  const gEmpty = g.bStart === g.bEnd, hEmpty = h.bStart === h.bEnd;
  if (!gEmpty && !hEmpty) return h.bStart < g.bEnd;
  if (gEmpty && hEmpty) return h.bStart === g.bStart;           // both inserted at the same spot
  if (hEmpty) return h.bStart > g.bStart && h.bStart < g.bEnd;    // an insert inside a changed stretch
  return false;                                                   // sorted: a change can't start before an insert point
}

/* ---------- blocks ---------- */

function mergeSeq(b, o, t, ctx) {
  return diff3(b, o, t, canon, (bs, os, ts) => resolveBlocks(bs, os, ts, ctx), ctx.stats);
}

function resolveBlocks(bs, os, ts, ctx) {
  if (bs.length === 1 && os.length === 1 && ts.length === 1) {
    const [bn, on, tn] = [bs[0], os[0], ts[0]];
    if (bn.type === on.type && on.type === tn.type) {
      try {
        const attrs = mergeAttrs(bn.attrs, on.attrs, tn.attrs);
        if (CONTAINERS.has(on.type)) {
          return [{ ...on, attrs, content: mergeSeq(bn.content || [], on.content || [], tn.content || [], ctx) }];
        }
        if (PROSE.has(on.type)) return [{ ...on, attrs, content: mergeInline(bn.content, on.content, tn.content) }];
        if (CODE.has(on.type)) return [{ ...on, attrs, content: mergeLines(bn.content, on.content, tn.content) }];
      } catch (err) {
        if (!(err instanceof Clash)) throw err;
      }
    }
  }
  const id = ctx.conflicts.length;
  ctx.conflicts.push({ id, base: bs, ours: os, theirs: ts });
  return [{ __conflict: id }];
}

function mergeAttrs(b = {}, o = {}, t = {}) {
  const out = {};
  for (const k of new Set([...Object.keys(b || {}), ...Object.keys(o || {}), ...Object.keys(t || {})])) {
    const [vb, vo, vt] = [JSON.stringify(b?.[k] ?? null), JSON.stringify(o?.[k] ?? null), JSON.stringify(t?.[k] ?? null)];
    if (vo === vt || vt === vb) out[k] = o?.[k] ?? null;
    else if (vo === vb) out[k] = t?.[k] ?? null;
    else throw new Clash();
  }
  return out;
}

/* ---------- inside a paragraph: word by word ---------- */

export function inlineTokens(content = []) {
  const out = [];
  for (const node of content || []) {
    if (node.type === 'text') {
      const mk = marksKey(node);
      for (const piece of node.text.match(/\s+|[^\s]+/g) || []) {
        out.push({ k: `${mk}\u0000${piece}`, text: piece, marks: node.marks });
      }
    } else {
      out.push({ k: canon(node), node });
    }
  }
  return out;
}

export function fromTokens(tokens) {
  const out = [];
  for (const tk of tokens) {
    if (tk.node) { out.push(tk.node); continue; }
    const last = out[out.length - 1];
    if (last?.type === 'text' && marksKey(last) === marksKey(tk) && last.__d === tk.d) last.text += tk.text;
    else {
      const n = { type: 'text', text: tk.text };
      if (tk.marks?.length) n.marks = tk.marks;
      if (tk.d) n.__d = tk.d;
      out.push(n);
    }
  }
  return out.map(n => n);
}

function mergeInline(b, o, t) {
  const merged = diff3(inlineTokens(b), inlineTokens(o), inlineTokens(t), x => x.k, () => { throw new Clash(); });
  return fromTokens(merged);
}

/* ---------- inside code: line by line ---------- */

function codeText(content) {
  return (content || []).map(n => n.text || '').join('');
}

function mergeLines(b, o, t) {
  const lines = s => s.match(/[^\n]*\n|[^\n]+$/g) || [];
  const merged = diff3(lines(codeText(b)), lines(codeText(o)), lines(codeText(t)), x => x, () => { throw new Clash(); });
  const text = merged.join('');
  return text ? [{ type: 'text', text }] : [];
}

/* ---------- conflicts → final doc ---------- */

function expand(node, conflicts, choices) {
  if (!node.content) return node;
  const content = [];
  for (const kid of node.content) {
    if (kid && kid.__conflict !== undefined) {
      const c = conflicts[kid.__conflict];
      const pick = choices[c.id] || 'both';
      const arr = pick === 'ours' ? c.ours : pick === 'theirs' ? c.theirs : pick === 'base' ? c.base : [...c.ours, ...c.theirs];
      content.push(...arr);
    } else {
      content.push(expand(kid, conflicts, choices));
    }
  }
  return { ...node, content };
}
